import { CheckCircleIcon, InfoIcon, WarningTwoIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Select,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '../../components/Card';
import CardBody from '../../components/Card/CardBody';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { checkAuthenticationSuperAdmin } from '../../helpers/lib';
import { useNotification } from '../../hooks';
import { NotificationService } from '../../services/notification/notification.service';

const Notification = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { listNotification } = useSelector((state) => state.home);

  // Check if user is super admin
  const isSuperAdmin = checkAuthenticationSuperAdmin();
  const {
    state: pushState,
    isSupported,
    requestPermission,
    subscribeToNotifications,
  } = useNotification();
  const { registerRefreshCallback } = useNotificationContext();

  const [, _setShowPushNotifications] = useState(false);

  // Notification permission state
  const [_notificationPermission, setNotificationPermission] = useState('default');
  const [showPermissionTip, setShowPermissionTip] = useState(false);

  // Filter states
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalItems: 0,
    hasMore: false,
  });
  const [filters, setFilters] = useState({
    category: '',
    is_read: undefined,
    page: 0,
    size: 10,
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Use refs to store current values to avoid dependency issues
  const filtersRef = useRef(filters);
  const debouncedFiltersRef = useRef(debouncedFilters);

  // Update refs when values change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    debouncedFiltersRef.current = debouncedFilters;
  }, [debouncedFilters]);

  // Request notification permission
  const handleRequestPermission = useCallback(async () => {
    try {
      console.log('🔍 Request permission clicked');
      console.log('🔍 Notification in window:', 'Notification' in window);
      console.log('🔍 Notification object:', Notification);
      console.log('🔍 requestPermission function:', typeof Notification.requestPermission);
      console.log('🔍 Notification.permission:', Notification.permission);
      console.log('🔍 navigator.permissions:', typeof navigator.permissions);
      console.log('🔍 Service Worker support:', 'serviceWorker' in navigator);

      // Try multiple approaches
      let permission = null;

      // Approach 1: Standard Notification.requestPermission
      if ('Notification' in window && typeof Notification.requestPermission === 'function') {
        console.log('🔍 Using standard Notification.requestPermission()...');
        try {
          permission = await Notification.requestPermission();
          console.log('🔍 Standard permission result:', permission);
        } catch (error) {
          console.log('🔍 Standard requestPermission failed:', error);
        }
      }

      // Approach 2: Try navigator.permissions API
      if (
        !permission &&
        navigator.permissions &&
        typeof navigator.permissions.query === 'function'
      ) {
        console.log('🔍 Trying navigator.permissions.query...');
        try {
          const result = await navigator.permissions.query({ name: 'notifications' });
          console.log('🔍 Permission query result:', result.state);

          if (result.state === 'granted') {
            permission = 'granted';
          } else if (result.state === 'prompt') {
            // Permission is prompt, show manual instructions
            console.log('🔍 Permission is prompt, showing manual instructions');
            permission = 'prompt';
          } else {
            permission = result.state;
          }
        } catch (error) {
          console.log('🔍 Permission query failed:', error);
        }
      }

      // Approach 3: Try service worker registration and push subscription
      if (!permission && 'serviceWorker' in navigator) {
        console.log('🔍 Trying service worker approach...');
        try {
          const registration = await navigator.serviceWorker.ready;
          if (registration.pushManager) {
            console.log('🔍 PushManager available, checking subscription...');
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              permission = 'granted';
              console.log('🔍 Already subscribed to push notifications');
            } else {
              console.log('🔍 No existing subscription, trying to subscribe...');
              try {
                // Try to subscribe to push notifications (this will trigger permission request)
                const newSubscription = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: null, // We'll let the browser handle VAPID key
                });
                if (newSubscription) {
                  permission = 'granted';
                  console.log('🔍 Successfully subscribed to push notifications');
                }
              } catch (error) {
                console.log('🔍 Push subscription failed:', error);
                if (error.name === 'NotAllowedError') {
                  permission = 'denied';
                } else {
                  permission = 'default';
                }
              }
            }
          }
        } catch (error) {
          console.log('🔍 Service worker approach failed:', error);
        }
      }

      // Set permission state
      if (permission) {
        setNotificationPermission(permission);
        console.log('🔍 Final permission state:', permission);

        if (permission === 'granted') {
          setShowPermissionTip(false);
          console.log('🔍 Permission granted, subscribing...');
          if (subscribeToNotifications) {
            await subscribeToNotifications();
          }
        } else if (permission === 'prompt') {
          console.log('🔍 Permission is prompt, showing manual instructions');
          setShowPermissionTip(true);
        } else {
          console.log('🔍 Permission not granted:', permission);
          setShowPermissionTip(true);
        }
      } else {
        console.log('🔍 No permission API available, showing manual instructions');
        setShowPermissionTip(true);
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      setShowPermissionTip(true);
    }
  }, [subscribeToNotifications]);

  // Check notification permission when drawer opens
  useEffect(() => {
    if (isOpen) {
      const checkPermission = () => {
        if ('Notification' in window) {
          const permission = Notification.permission;
          setNotificationPermission(permission);

          // Show tip if permission is denied, default, or undefined (especially for Edge)
          const isEdge = navigator.userAgent.includes('Edg');
          const shouldShowTip =
            isEdge &&
            (permission === 'denied' || permission === 'default' || permission === undefined);

          if (shouldShowTip) {
            setShowPermissionTip(true);
          } else {
            setShowPermissionTip(false);
          }
        } else {
          // Notification not supported
          setShowPermissionTip(false);
        }
      };

      checkPermission();
    }
  }, [isOpen]);

  // Load notifications with filters
  const _loadNotifications = useCallback(
    async (isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        // Use debounced filters to avoid infinite loops
        const apiFilters = { ...debouncedFilters, language: i18n.language };
        if (!apiFilters.category) {
          delete apiFilters.category;
        }

        const response = await NotificationService.getNotificationsFilter(apiFilters);
        const newNotifications = response.data || [];

        if (isLoadMore) {
          setNotifications((prev) => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        // Update pagination info
        setPagination({
          currentPage: apiFilters.page,
          totalPages: response.total_page || 1,
          totalItems: response.total_item || 0,
          hasMore: (response.total_page || 1) > apiFilters.page + 1,
        });
      } catch (error) {
        console.error('Failed to load notifications:', error);
        if (!isLoadMore) {
          setNotifications([]);
        }
      } finally {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [debouncedFilters, i18n.language]
  );

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();

      // Reload notifications to update status
      const apiFilters = { ...debouncedFilters, language: i18n.language };
      if (!apiFilters.category) {
        delete apiFilters.category;
      }

      const response = await NotificationService.getNotificationsFilter(apiFilters);
      const newNotifications = response.data || [];

      setNotifications(newNotifications);

      // Update pagination info
      setPagination({
        currentPage: apiFilters.page,
        totalPages: response.total_page || 1,
        totalItems: response.total_item || 0,
        hasMore: (response.total_page || 1) > apiFilters.page + 1,
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Debounce filters to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [filters]);

  // Load notifications when drawer opens or debounced filters change
  useEffect(() => {
    if (isOpen) {
      // Reset to page 0 when filter changes
      setFilters((prev) => ({ ...prev, page: 0 }));
      // Call loadNotifications directly to avoid dependency loop
      const loadNotificationsDirect = async () => {
        setLoading(true);
        try {
          const apiFilters = { ...debouncedFiltersRef.current, page: 0, language: i18n.language };
          if (!apiFilters.category) {
            delete apiFilters.category;
          }

          const response = await NotificationService.getNotificationsFilter(apiFilters);
          const newNotifications = response.data || [];

          setNotifications(newNotifications);

          // Update pagination info
          setPagination({
            currentPage: apiFilters.page,
            totalPages: response.total_page || 1,
            totalItems: response.total_item || 0,
            hasMore: (response.total_page || 1) > apiFilters.page + 1,
          });
        } catch (error) {
          console.error('Failed to load notifications:', error);
          setNotifications([]);
        } finally {
          setLoading(false);
        }
      };

      loadNotificationsDirect();
    }
  }, [isOpen, debouncedFilters.category, debouncedFilters.is_read, i18n.language]);

  // Register with global notification context for push notification events
  useEffect(() => {
    const refreshNotifications = async (data) => {
      // Check if we should refresh based on category match
      const shouldRefresh = (() => {
        if (!data || !data.category) return false;

        const currentFilter = filtersRef.current.category;
        const isAllFilter = !currentFilter || currentFilter === '';
        const isMatchingCategory = currentFilter === data.category;

        return isAllFilter || isMatchingCategory;
      })();

      // Only refresh if drawer is open and category matches
      if (isOpen && shouldRefresh) {
        setLoading(true);
        setFilters((prev) => ({ ...prev, page: 0 }));

        try {
          const apiFilters = { ...filtersRef.current, page: 0, language: i18n.language };
          if (!apiFilters.category) {
            delete apiFilters.category;
          }

          const response = await NotificationService.getNotificationsFilter(apiFilters);
          const newNotifications = response.data || [];

          setNotifications(newNotifications);

          setPagination({
            currentPage: 0,
            totalPages: response.total_page || 1,
            totalItems: response.total_item || 0,
            hasMore: (response.total_page || 1) > 1,
          });
        } catch (error) {
          console.error('Failed to load notifications:', error);
          setNotifications([]);
        } finally {
          setLoading(false);
        }
      }
    };

    // Register refresh callback with global context
    const unregister = registerRefreshCallback(refreshNotifications);

    // Cleanup: unregister callback when component unmounts or drawer closes
    return () => {
      unregister();
    };
  }, [isOpen, registerRefreshCallback, i18n.language]);

  // Load more notifications
  const handleLoadMore = useCallback(() => {
    const nextPage = filters.page + 1;
    setFilters((prev) => ({ ...prev, page: nextPage }));

    // Load more directly without triggering useEffect
    const loadMoreData = async () => {
      setLoadingMore(true);
      try {
        const apiFilters = { ...filters, page: nextPage, language: i18n.language };
        if (!apiFilters.category) {
          delete apiFilters.category;
        }

        const response = await NotificationService.getNotificationsFilter(apiFilters);
        const newNotifications = response.data || [];

        setNotifications((prev) => [...prev, ...newNotifications]);

        // Update pagination info
        setPagination({
          currentPage: nextPage,
          totalPages: response.total_page || 1,
          totalItems: response.total_item || 0,
          hasMore: (response.total_page || 1) > nextPage + 1,
        });
      } catch (error) {
        console.error('Failed to load more notifications:', error);
      } finally {
        setLoadingMore(false);
      }
    };

    loadMoreData();
  }, [filters, i18n.language]);

  // Combine server notifications only (push notifications are handled by browser)
  const allNotifications = (listNotification || [])
    .map((item, index) => ({
      id: `server-${index}`,
      title: 'Server Notification',
      body: item.message?.en || '',
      timestamp: new Date(item?.created_date).getTime(),
      read: false,
      type: 'server',
    }))
    .sort((a, b) => b.timestamp - a.timestamp);

  const unreadCount = allNotifications.filter((notif) => !notif.read).length;

  // Format time for display
  const formatTime = (dateString) => {
    const date = moment(dateString);
    const now = moment();
    const diffInMinutes = now.diff(date, 'minutes');

    if (diffInMinutes < 1) {
      return t('notification.just_now');
    } else if (diffInMinutes < 60) {
      return t('notification.minutes_ago', { count: diffInMinutes });
    } else if (diffInMinutes < 1440) {
      // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return t('notification.hours_ago', { count: hours });
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return t('notification.days_ago', { count: days });
    }
  };

  // Format detailed time for tooltip
  const formatDetailedTime = (dateString) => {
    return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  // Get icon by level
  const getLevelIcon = (level) => {
    switch (level) {
      case 'Information':
        return InfoIcon;
      case 'Warning':
        return WarningTwoIcon;
      case 'Success':
        return CheckCircleIcon;
      case 'Error':
        return WarningTwoIcon;
      default:
        return InfoIcon;
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='md'>
        <DrawerOverlay bg='blackAlpha.300' backdropFilter='blur(4px)' />
        <DrawerContent bg='bg_box'>
          <DrawerCloseButton
            color='color_main_text'
            _hover={{ color: 'color_main_text', bg: 'menu_item_option_hover' }}
            size='lg'
          />
          <DrawerHeader py={4} bg='bg_box' borderColor='border_gray_solid' borderBottom='1px solid'>
            <HStack align='center' justify='space-between'>
              <Text color='color_main_text' fontSize='lg' fontWeight='bold'>
                🔔 {t('notification.notifications')}
              </Text>
              {unreadCount > 0 && (
                <Badge
                  px={2}
                  py={1}
                  fontSize='xs'
                  fontWeight='bold'
                  borderRadius='full'
                  colorScheme='red'
                >
                  {unreadCount}
                </Badge>
              )}
            </HStack>
          </DrawerHeader>

          <DrawerBody flexDir='column' display='flex' p={0} bg='bg_main_container'>
            {/* Fixed Filter Section */}
            <Box
              p={4}
              bg='bg_main_container'
              borderColor='border_gray_solid'
              borderBottom='1px solid'
            >
              <VStack align='stretch' spacing={4}>
                {/* Permission Warning */}
                {isSupported && pushState.permission === 'denied' && (
                  <Alert borderRadius='md' status='warning'>
                    <AlertIcon />
                    <VStack align='start' spacing={2}>
                      <AlertTitle fontSize='sm'>
                        {t('notification.permission_denied_title')}
                      </AlertTitle>
                      <AlertDescription fontSize='xs'>
                        {t('notification.permission_denied_body')}
                      </AlertDescription>
                    </VStack>
                  </Alert>
                )}

                {/* Safari-specific notification request */}
                {isSupported &&
                  pushState.permission === 'default' &&
                  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && (
                    <Alert borderRadius='md' status='info'>
                      <AlertIcon />
                      <VStack align='start' spacing={3}>
                        <AlertTitle fontSize='sm'>
                          🔔 {t('notification.safari_enable_title')}
                        </AlertTitle>
                        <AlertDescription fontSize='xs'>
                          {t('notification.safari_enable_body')}
                        </AlertDescription>
                        <HStack spacing={2}>
                          <Button
                            colorScheme='blue'
                            isLoading={pushState.loading}
                            loadingText={t('common.loading')}
                            onClick={async () => {
                              const success = await requestPermission();
                              if (success) {
                                await subscribeToNotifications();
                              }
                            }}
                            size='sm'
                          >
                            {t('notification.safari_enable_button')}
                          </Button>
                          <Button
                            onClick={() =>
                              window.open(
                                'https://support.apple.com/guide/safari/notifications-sfri40734/mac',
                                '_blank'
                              )
                            }
                            size='sm'
                            variant='outline'
                          >
                            {t('notification.safari_help_button')}
                          </Button>
                        </HStack>
                      </VStack>
                    </Alert>
                  )}

                {/* Edge-specific notification permission tip */}
                {showPermissionTip && (
                  <Alert borderRadius='md' status='info'>
                    <AlertIcon />
                    <VStack align='start' spacing={3}>
                      <AlertTitle fontSize='sm'>
                        🔔 {t('notification.edge_enable_title')}
                      </AlertTitle>
                      <AlertDescription fontSize='xs'>
                        {t('notification.edge_enable_body')}
                      </AlertDescription>
                    </VStack>
                  </Alert>
                )}

                {/* Filter Controls */}
                <Box
                  p={3}
                  bg='bg_box'
                  border='1px solid'
                  borderColor='border_gray_solid'
                  borderRadius='md'
                >
                  <VStack align='stretch' spacing={3}>
                    <Text color='color_main_text' fontSize='sm' fontWeight='semibold'>
                      {t('notification.filters')}
                    </Text>

                    <HStack spacing={3}>
                      <Box flex={1}>
                        <Text mb={1} color='color_main_text' fontSize='xs' fontWeight='medium'>
                          {t('notification.filter_by_category')}
                        </Text>
                        <Select
                          color='color_main_text'
                          bg='bg_main_input'
                          borderColor='border_gray_solid'
                          _hover={{ borderColor: 'blue.400' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, category: e.target.value }))
                          }
                          size='sm'
                          value={filters.category}
                        >
                          <option value=''>{t('notification.all')}</option>
                          <option value='Limitation'>{t('notification.limitation')}</option>
                          <option value='Synchronization'>
                            {t('notification.synchronization')}
                          </option>
                          {isSuperAdmin && (
                            <option value='Organization'>{t('notification.organization')}</option>
                          )}
                        </Select>
                      </Box>
                      <Box flex={1}>
                        <Text mb={1} color='color_main_text' fontSize='xs' fontWeight='medium'>
                          {t('notification.filter_by_status')}
                        </Text>
                        <Select
                          color='color_main_text'
                          bg='bg_main_input'
                          borderColor='border_gray_solid'
                          _hover={{ borderColor: 'blue.400' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              is_read:
                                e.target.value === '' ? undefined : e.target.value === 'true',
                            }))
                          }
                          size='sm'
                          value={filters.is_read === undefined ? '' : filters.is_read.toString()}
                        >
                          <option value=''>{t('notification.all')}</option>
                          <option value='false'>{t('notification.unread')}</option>
                          <option value='true'>{t('notification.read')}</option>
                        </Select>
                      </Box>
                    </HStack>

                    <HStack justify='flex-end'>
                      <Button
                        px={4}
                        borderRadius='md'
                        _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                        transition='all 0.2s'
                        colorScheme='blue'
                        isLoading={loading}
                        onClick={handleMarkAllAsRead}
                        size='sm'
                        variant='solid'
                      >
                        {t('notification.mark_all_read')}
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Scrollable Content Section */}
            <Box flex={1} overflowY='auto' p={4}>
              {/* Notifications List */}
              {loading ? (
                <Box py={8} textAlign='center'>
                  <Spinner color='blue.500' size='md' thickness='3px' />
                  <Text mt={3} color='color_main_text' fontSize='sm'>
                    {t('notification.loading')}
                  </Text>
                </Box>
              ) : notifications.length === 0 ? (
                <Box py={8} textAlign='center'>
                  <Text mb={2} color='color_main_text' fontSize='md'>
                    📭
                  </Text>
                  <Text color='color_main_text' fontSize='sm'>
                    {t('notification.no_notifications')}
                  </Text>
                </Box>
              ) : (
                <VStack align='stretch' spacing={3}>
                  {notifications.map((item) => {
                    const isRead = item.read_at !== null;
                    return (
                      <Card
                        key={item.id}
                        bg={isRead ? 'bg_box' : 'bg_conversation_item_select'}
                        border='1px solid'
                        borderColor={isRead ? 'border_gray_solid' : 'border_main'}
                        borderRadius='lg'
                        boxShadow={
                          isRead
                            ? '0 1px 3px rgba(0, 0, 0, 0.05)'
                            : '0 2px 8px rgba(59, 130, 246, 0.15)'
                        }
                        cursor='pointer'
                        transition='all 0.2s'
                        _hover={{
                          transform: 'translateY(-1px)',
                          boxShadow: isRead
                            ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                            : '0 4px 15px rgba(59, 130, 246, 0.25)',
                        }}
                        position='relative'
                        overflow='visible'
                      >
                        <CardBody p={4} position='relative'>
                          {!isRead && (
                            <Box
                              pos='absolute'
                              top={2}
                              right={2}
                              w='2'
                              h='2'
                              bg='yellow.400'
                              borderRadius='full'
                            />
                          )}
                          <VStack align='stretch' spacing={2}>
                            {/* Header with icon and title */}
                            <HStack align='start' flex={1} spacing={1}>
                              <Icon
                                as={getLevelIcon(item.level)}
                                boxSize={4}
                                mt={0.5}
                                color='white'
                              />
                              <Text
                                flex={1}
                                color='color_main_text'
                                fontSize='md'
                                fontWeight={isRead ? 'medium' : 'bold'}
                                lineHeight='1.3'
                              >
                                {item.title}
                              </Text>
                            </HStack>

                            {/* Body */}
                            <Text color='color_main_text' fontSize='sm' lineHeight='1.4'>
                              {item.body}
                            </Text>

                            {/* Bottom row with icon and time */}
                            <HStack align='center' justify='space-between'>
                              <Badge
                                px={2}
                                py={1}
                                fontSize='xs'
                                borderRadius='full'
                                colorScheme={item.category === 'Limitation' ? 'red' : 'blue'}
                                size='sm'
                                variant='subtle'
                              >
                                {item.category === 'Limitation'
                                  ? t('notification.limitation')
                                  : item.category === 'Synchronization'
                                    ? t('notification.synchronization')
                                    : item.category}
                              </Badge>
                              <Tooltip
                                px={3}
                                py={2}
                                color='white'
                                fontSize='sm'
                                bg='gray.800'
                                borderRadius='md'
                                hasArrow
                                label={formatDetailedTime(item.sent_at)}
                                placement='top'
                              >
                                <Text
                                  color='color_secondary_text'
                                  fontSize='xs'
                                  fontWeight='normal'
                                  _hover={{ color: 'color_main_text' }}
                                  cursor='pointer'
                                >
                                  {formatTime(item.sent_at)}
                                </Text>
                              </Tooltip>
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    );
                  })}
                </VStack>
              )}

              {/* Load More Button */}
              {pagination.hasMore && (
                <Box py={4} textAlign='center'>
                  <Button
                    _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                    transition='all 0.2s'
                    colorScheme='blue'
                    isLoading={loadingMore}
                    loadingText='Loading...'
                    onClick={handleLoadMore}
                    size='md'
                    variant='outline'
                  >
                    Load More Notifications
                  </Button>
                </Box>
              )}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Notification;
